import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Mic, Square, Loader2, Radio, FileText, Sparkles, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AttendeePicker from "@/components/AttendeePicker";

function fmtElapsed(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function MeetingListener() {
  const { mid } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [micError, setMicError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [summarizing, setSummarizing] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState("");
  const [prep, setPrep] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);

  const recRef = useRef(null);
  const listeningRef = useRef(false);
  const finalRef = useRef("");
  const startRef = useRef(0);
  const scrollRef = useRef(null);

  // Load meeting
  useEffect(() => {
    api.get(`/meetings/${mid}`)
      .then((r) => {
        setMeeting(r.data);
        finalRef.current = r.data.transcript || "";
        setTranscript(r.data.transcript || "");
      })
      .catch(() => { toast.error("Meeting not found."); navigate("/meetings"); });
  }, [mid]);

  // Elapsed timer
  useEffect(() => {
    startRef.current = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript]);

  // Auto-detect attendees → one-line prep brief per person (regenerates when attendees change)
  const attendeesKey = (meeting?.attendees || []).join("|");
  useEffect(() => {
    if (!attendeesKey) return undefined;
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setPrepLoading(true);
      try {
        const r = await api.get(`/meetings/${mid}/prep`);
        if (!cancelled) setPrep(r.data);
      } catch { /* ignore */ }
      finally { if (!cancelled) setPrepLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [mid, attendeesKey]);

  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (e) => {
      let fin = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) fin += `${t} `; else interim += t;
      }
      if (fin) finalRef.current = `${finalRef.current} ${fin}`.replace(/\s+/g, " ").trim();
      setTranscript(`${finalRef.current} ${interim}`.trim());
    };
    r.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setMicError("Microphone access is blocked here. Open the app in a browser tab and allow the mic — or add the transcript manually below.");
        listeningRef.current = false;
        setListening(false);
      }
    };
    r.onend = () => {
      // Browsers stop continuous recognition periodically — restart while intended.
      if (listeningRef.current) { try { r.start(); } catch { /* already running */ } }
      else setListening(false);
    };
    recRef.current = r;
    listeningRef.current = true;
    try { r.start(); setListening(true); setMicError(""); } catch { /* already running */ }
  }, []);

  const stopListening = useCallback(() => {
    listeningRef.current = false;
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setListening(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => { listeningRef.current = false; try { recRef.current?.stop(); } catch { /* ignore */ } }, []);

  const handleAttendeeChange = async (next) => {
    const current = meeting?.attendees || [];
    const added = next.filter((n) => !current.some((a) => a.toLowerCase() === n.toLowerCase()));
    for (const name of added) {
      try {
        const r = await api.post(`/meetings/${mid}/attendees`, { name });
        setMeeting(r.data);
      } catch { toast.error(`Couldn't add ${name}.`); }
    }
  };

  const addManual = () => {
    const t = manual.trim();
    if (!t) return;
    finalRef.current = `${finalRef.current}\n${t}`.trim();
    setTranscript(finalRef.current);
    setManual("");
    setShowManual(false);
    toast.success("Added to transcript.");
  };

  const finish = async () => {
    if (summarizing) return;
    stopListening();
    const text = finalRef.current.trim();
    if (!text) {
      toast.error("Nothing captured yet — say something or add a transcript first.");
      return;
    }
    setSummarizing(true);
    try {
      await api.post(`/meetings/${mid}/transcript`, { transcript: text });
      await api.post(`/meetings/${mid}/summarize`);
      toast.success("Summarized and saved to your Vault.");
      navigate(`/meetings/${mid}`);
    } catch {
      toast.error("Couldn't summarize the meeting.");
      setSummarizing(false);
    }
  };

  if (!meeting) {
    return (
      <>
        <div className="surface-header">
          <button onClick={() => navigate("/meetings")} className="btn btn-ghost" data-testid="back-to-meetings">
            <ChevronLeft size={14} /> Meetings
          </button>
        </div>
        <div style={{ padding: 32 }} className="text-dim">Loading…</div>
      </>
    );
  }

  return (
    <>
      <div className="surface-header" style={{ paddingBottom: 14 }}>
        <button onClick={() => navigate("/meetings")} className="btn btn-ghost" data-testid="back-to-meetings" style={{ fontSize: 13 }}>
          <ChevronLeft size={16} /> Meetings
        </button>
        <span
          className="status-pill"
          data-testid="listener-status"
          style={listening ? { borderColor: "var(--ph-product-teal)", color: "var(--ph-product-teal)" } : undefined}
        >
          <span className="dot" style={listening ? { background: "var(--ph-product-teal)" } : undefined} />
          {listening ? "Listening" : "Paused"} · {fmtElapsed(elapsed)}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "8px clamp(14px, 4vw, 32px) 60px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }} className="fade-up">

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ph-product-teal)", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
              <Radio size={13} /> Live meeting
            </div>
            <h1 data-testid="listener-title" style={{ fontSize: 30, margin: "8px 0 4px" }}>{meeting.title}</h1>
            {meeting.location && <div className="text-dim" style={{ fontSize: 12.5 }}>{meeting.location}</div>}
          </div>

          {/* Attendees */}
          <div style={{ marginTop: 22 }}>
            <div className="section-label" style={{ marginBottom: 10 }}>Attendees</div>
            <AttendeePicker value={meeting.attendees || []} onChange={handleAttendeeChange} removable={false} placeholder="Add someone who joined…" />
            <div className="text-dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              New names become a People profile + a wiki-linked Vault note automatically.
            </div>
          </div>

          {/* Prep brief — one line per attendee, auto-written from their People note */}
          {(meeting.attendees || []).length > 0 && (
            <div style={{ marginTop: 24 }} data-testid="prep-brief-section">
              <div className="section-label" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={13} style={{ color: "var(--ph-accent-purple)" }} />
                Prep brief
                <span style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-dim)", border: "1px solid var(--hairline)", borderRadius: 999, padding: "1px 7px" }}>auto</span>
                {prepLoading && <Loader2 size={12} className="spin" style={{ color: "var(--ink-dim)" }} />}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(prep?.attendees || (meeting.attendees || []).map((n) => ({ name: n }))).map((a) => (
                  <PrepRow key={a.name} a={a} loading={prepLoading && !prep} navigate={navigate} />
                ))}
              </div>
            </div>
          )}

          {/* Mic controls */}
          <div style={{ marginTop: 26, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {!listening ? (
              <button className="btn btn-primary" data-testid="start-listening-btn" onClick={startListening} disabled={!supported}>
                <Mic size={15} /> {transcript ? "Resume listening" : "Start listening"}
              </button>
            ) : (
              <button className="btn" data-testid="pause-listening-btn" onClick={stopListening} style={{ borderColor: "var(--ph-product-teal)" }}>
                <Square size={14} /> Pause
              </button>
            )}
            <button className="btn btn-ghost" data-testid="toggle-manual-btn" onClick={() => setShowManual((v) => !v)}>
              <FileText size={14} /> Add transcript manually
            </button>
            <button
              className="btn btn-primary"
              data-testid="stop-summarize-btn"
              onClick={finish}
              disabled={summarizing}
              style={{ marginLeft: "auto", background: "var(--ph-accent-purple)", borderColor: "var(--ph-accent-purple)" }}
            >
              {summarizing ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
              {summarizing ? "Summarizing…" : "Stop & Summarize"}
            </button>
          </div>

          {micError && (
            <div data-testid="mic-error" style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, background: "rgba(255,176,32,0.08)", border: "1px solid rgba(255,176,32,0.4)", color: "var(--ink-soft)", fontSize: 13 }}>
              <AlertCircle size={15} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 1 }} />
              {micError}
            </div>
          )}
          {!supported && (
            <div className="text-dim" style={{ marginTop: 14, fontSize: 13 }}>
              Live transcription needs a Chromium-based browser. You can still add the transcript manually.
            </div>
          )}

          {/* Manual transcript entry */}
          {showManual && (
            <div style={{ marginTop: 16 }}>
              <textarea
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="Paste or type what was said…"
                data-testid="manual-transcript-input"
                rows={4}
                style={{
                  width: "100%", boxSizing: "border-box", padding: 12, borderRadius: 10,
                  border: "1px solid var(--hairline)", background: "var(--panel-2)", color: "var(--ink)",
                  fontSize: 13.5, fontFamily: "inherit", lineHeight: 1.6, outline: "none", resize: "vertical",
                }}
              />
              <button className="btn" data-testid="add-manual-btn" onClick={addManual} style={{ marginTop: 8 }}>
                Add to transcript
              </button>
            </div>
          )}

          {/* Live transcript */}
          <div style={{ marginTop: 26 }}>
            <div className="section-label" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              Transcript
              {listening && <span style={{ width: 7, height: 7, borderRadius: 50, background: "var(--ph-product-teal)", display: "inline-block", animation: "pulse 1.4s ease-in-out infinite" }} />}
            </div>
            <div
              ref={scrollRef}
              data-testid="live-transcript"
              style={{
                minHeight: 160, maxHeight: 360, overflowY: "auto", padding: 18, borderRadius: 12,
                background: "var(--panel-2)", border: "1px solid var(--hairline)",
                fontSize: 14.5, lineHeight: 1.8, color: "var(--ink-soft)", whiteSpace: "pre-wrap",
              }}
            >
              {transcript || <span className="text-dim">{listening ? "Listening… start speaking." : "Press Start listening, or add the transcript manually."}</span>}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

function PrepRow({ a, loading, navigate }) {
  const initials = a.initials || (a.name?.[0] || "?").toUpperCase();
  const hasMeta = a.last_meeting?.date || a.open_action_count > 0;
  return (
    <div
      data-testid={`prep-${a.name}`}
      style={{
        display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px",
        background: "var(--panel-2)", border: "1px solid var(--hairline)",
        borderLeft: `3px solid ${a.color || "#7B6FE8"}`, borderRadius: 11,
      }}
    >
      <span className="avatar-circle" style={{ width: 30, height: 30, fontSize: 12, background: a.color || "#6B5CE7", flexShrink: 0 }}>
        {initials}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink)" }}>{a.name}</span>
          {a.role && <span className="text-dim" style={{ fontSize: 11.5 }}>{a.role}</span>}
          {a.known === false && (
            <span style={{ fontSize: 9.5, letterSpacing: "0.08em", color: "var(--ph-product-teal)", border: "1px solid rgba(78,205,196,0.45)", borderRadius: 999, padding: "1px 6px" }}>
              NEW
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4, lineHeight: 1.5 }}>
          {a.brief
            ? a.brief
            : <span className="text-dim">{loading ? "Preparing brief…" : "No brief available."}</span>}
        </div>
        {hasMeta && (
          <div className="text-dim" style={{ fontSize: 11, marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {a.last_meeting?.date && <span>Last met {String(a.last_meeting.date).slice(0, 10)}</span>}
            {a.open_action_count > 0 && (
              <span style={{ color: "var(--ph-accent-purple)", fontWeight: 600 }}>{a.open_action_count} open</span>
            )}
          </div>
        )}
      </div>
      {a.vault_doc_id && (
        <button
          className="btn btn-ghost"
          style={{ fontSize: 11.5, padding: "4px 8px", flexShrink: 0 }}
          onClick={() => navigate(`/vault/${a.vault_doc_id}`)}
          data-testid={`prep-view-${a.name}`}
        >
          View
        </button>
      )}
    </div>
  );
}
